import type { IOGateway } from '@interopio/gateway';
import { getPipeFileName } from './common.ts';
import type { PipeNameProperties, PipeServerOptions } from './types.ts';
import { createServer } from 'node:net';

interface AuthInfo {
    user: string;
    username?: string;
    token?: string;
    headers?: { [key: string]: string };
}

export type GatewayPipeCommand
    = { command: 'shutdown' }
    | { command: 'update-auth', auth: AuthInfo }
    | { command: 'get-info' };


export function getGWPipeFileName({ environment, region, user }: PipeNameProperties) {
    return getPipeFileName(`glue42-gateway-${environment}-${region}-${user}`);
}

function createPipeCommandServer({ logger, pipeName, handler, onClose }: {
    pipeName: string,
    logger: IOGateway.Logging.Logger,
    handler: (msg: GatewayPipeCommand) => Promise<unknown>,
    onClose?: () => void
}) {
    if (logger.enabledFor('info')) {
        logger.info(`[${pipeName}] opening command server pipe, waiting for the data...`);
    }
    try {
        const server = createServer((stream) => {
            if (logger.enabledFor('debug')) {
                logger.debug(`[${pipeName}] command pipe server got client, waiting for the data...`);
            }
            stream.on('close', () => {
                if (logger.enabledFor('debug')) {
                    logger.debug(`[${pipeName}] client closed`);
                }
            });
            stream.on('error', (error) => {
                logger.error(`[${pipeName}] error on pipe stream`, error);
            });
            stream.on("data", async (...args) => {
                try {
                    const asString = args.toString();
                    const msg = JSON.parse(asString) as GatewayPipeCommand;
                    if (logger.enabledFor('info')) {
                        logger.info(`[${pipeName}] received command ${msg.command}`);
                    }
                    handler(msg)
                        .then((result) => {
                            const response = JSON.stringify({ result });
                            if (logger.enabledFor('info')) {
                                logger.info(`[${pipeName}] command ${msg.command} processed successfully. sending : ${response}`);
                            }
                            stream.write(response, (e) => {
                                if (e) {
                                    if (logger.enabledFor('debug')) {
                                        logger.debug(`[${pipeName}] error while sending response for command ${msg.command}`, e);
                                    }
                                }
                            });

                        })
                        .catch((error: { message: string }) => {
                            const response = JSON.stringify({ error: error.message });
                            logger.error(`[${pipeName}] processing command ${msg.command} failed, sending ${response}`, error);
                            stream.write(response, (e) => {
                                if (e) {
                                    if (logger.enabledFor('debug')) {
                                        logger.debug(`[${pipeName}] error while sending error response for command ${msg.command}`, e);
                                    }
                                }
                            });
                        })
                        .finally(() => {
                            if (msg.command === "shutdown") {
                                if (logger.enabledFor('info')) {
                                    logger.info(`[${pipeName}] stopping management pipe server`);
                                }
                                server.close(onClose);
                            }
                        });
                }
                catch (error) {
                    logger.error(`[${pipeName}] unable to parse the data`, error);
                }
            });
        });
        server.on("listening", () => {
            if (logger.enabledFor('info')) {
                logger.info(`[${pipeName}] connected to the pipe, waiting for the data...`);
            }
        });
        server.on("error", (error) => {
            logger.error(`[${pipeName}] error while opening the pipe - ${pipeName}`, error);
            throw error;
        });
        server.listen(pipeName);
    }
    catch (cause) {
        const msg = `error while opening the pipe - ${pipeName}`;
        logger.error(msg, cause);
        const error = new Error(msg) as Error & { cause?: unknown };
        error.cause = cause;
        throw error;
    }
}

export function gatewayCommandPipeServer({ logger, shutdownHandler, info, pipeNameProps }: PipeServerOptions) {
    const pipeName = getGWPipeFileName(pipeNameProps);
    createPipeCommandServer({
        pipeName,
        logger,
        onClose: () => {
            if (logger.enabledFor('info')) {
                logger.info(`[${pipeName}] command server closed, exiting bridge process`);
            }
            process.exit(0);
        },
        handler: async (msg) => {
            if (msg.command === "shutdown") {
                await shutdownHandler?.();
                return "gateway stopped";
            }
            else if (msg.command === "get-info") {
                return info;
            }
            else if (msg.command === "update-auth") {
                if (msg.auth) {
                    throw new Error("auth update not implemented");
                }
                else {
                    throw new Error("no auth provided");
                }
            }
        }
    });
}
